<template>
  <div class="ip" :class="{ 'ip--editing': editing, 'ip--highlight': editing && highlight }">
    <!-- Se edita siempre en el lugar; con «Editar» el aviso y los contornos quedan a la vista.
         Lo que la ficha no edita se hace desde «Vista anterior» (formulario completo). -->
    <div v-if="editing && highlight && activeTab === 'profile'" class="ip-edit-hint">
      <i class="fas fa-mouse-pointer me-2"></i>
      <span>{{ $t("itemProfile.editHint") }}</span>
    </div>

    <!-- Obligatorios vacíos (se guardó con «Guardar de todos modos» o es de antes): cada
         nombre abre su campo para completarlo acá (#473). -->
    <div v-if="missingRequired.length && activeTab === 'profile'" class="ip-missing" role="alert">
      <i class="fas fa-exclamation-triangle me-1"></i>
      <span>{{ $t("missingRequired.summary") }}:</span>
      <template v-for="(f, idx) in missingRequired">
        <button
          v-if="canFillMissing(f)"
          :key="'missing-' + f.key"
          type="button"
          class="btn btn-link mb-0 p-0 ip-missing__field"
          @click="fillMissing(f.key)"
        >{{ $t(f.label) }}</button>
        <strong v-else :key="'missing-' + f.key">{{ $t(f.label) }}</strong>
        <span v-if="idx < missingRequired.length - 1" :key="'missing-sep-' + f.key">,</span>
      </template>
    </div>

    <!-- Encabezado: qué es, sus etiquetas y lo que más se consulta -->
    <section class="card ip-hero">
      <div class="ip-hero__body">
        <!-- Pago / cobro: el «logo» es la dirección del dinero -->
        <div
          v-if="isTransaction"
          class="ip-hero__avatar ip-tx__dir"
          :class="txDirection === 'in' ? 'ip-tx__dir--in' : 'ip-tx__dir--out'"
        >
          <i :class="txDirection === 'in' ? 'fas fa-arrow-down' : 'fas fa-arrow-up'"></i>
        </div>
        <div v-else class="ip-hero__avatar" :class="{ 'ip-hero__avatar--img': avatarSrc }">
          <img v-if="avatarSrc" :src="avatarSrc" :alt="item.name || ''" />
          <i v-else-if="layout.avatarIcon" :class="layout.avatarIcon"></i>
          <span v-else>{{ initials }}</span>
        </div>

        <div class="ip-hero__main">
          <template v-if="isTransaction">
            <div class="ip-tx__kind">
              <span>{{ $t(txKindLabel) }}</span>
              <span
                v-if="txStatus"
                class="ip-tx__status"
                :class="'ip-tx__status--' + txStatus.tone"
              >
                <i :class="txStatus.icon" class="me-1"></i>{{ $t(txStatus.label) }}
              </span>
            </div>
            <!-- Nombre: título arriba, sin rótulo; si se edita, con el contorno y el lápiz al pasar el mouse -->
            <ItemProfileField
              v-if="heroField('name') && nameEditable"
              v-bind="fieldProps(heroField('name'))"
              bare
              :show-label="false"
              v-on="fieldListeners"
            >
              <h4 class="ip-tx__title--lead">{{ item.name }}</h4>
            </ItemProfileField>
            <h4 v-else-if="heroField('name')" class="ip-tx__title--lead">{{ item.name }}</h4>
            <!-- Editando, el número grande y la moneda son sus campos -->
            <div v-if="editing && (amountField || currencyField)" class="ip-tx__amount-edit">
              <ItemProfileField
                v-if="amountField"
                v-bind="fieldProps(amountField)"
                bare
                class="ip-tx__amount-field"
                :show-label="true"
                v-on="fieldListeners"
              >
                <h3 class="ip-tx__amount">{{ formattedAmount }}</h3>
              </ItemProfileField>
              <ItemProfileField
                v-if="currencyField"
                v-bind="fieldProps(currencyField)"
                bare
                class="ip-tx__currency-field"
                :show-label="true"
                v-on="fieldListeners"
              />
            </div>
            <h3 v-else class="ip-tx__amount">
              {{ formattedAmount }}
              <small v-if="currencyLabel">{{ currencyLabel }}</small>
            </h3>
            <div v-if="amountNotes.length" class="ip-tx__notes">
              <span v-for="n in amountNotes" :key="'note-' + n.key" class="ip-tx__note">
                {{ n.label }}
                <strong>{{ n.value }}</strong>
              </span>
            </div>
          </template>
          <ItemProfileField
            v-else-if="heroField('name')"
            v-bind="fieldProps(heroField('name'))"
            bare
            :show-label="editing && nameEditable"
            v-on="fieldListeners"
          >
            <h3 class="ip-hero__name">{{ item.name }}</h3>
          </ItemProfileField>

          <!-- Etiquetas y teléfono / email / web en un mismo renglón. -->
          <!-- Editando, etiquetas, contactos y fechas van a la tarjeta «General» (generalFields) -->
          <div v-if="!editing && (tagFields.length || reachFields.length)" class="ip-hero__tagline">
            <div v-if="tagFields.length" class="ip-hero__tags">
              <ItemProfileField
                v-for="f in tagFields"
                :key="'tag-' + f.key"
                v-bind="fieldProps(f)"
                bare
                class="ip-hero__tag"
                :show-label="editing"
                v-on="fieldListeners"
              />
            </div>
            <div v-if="reachFields.length" class="ip-hero__reach">
              <ItemProfileField
                v-for="r in reachFields"
                :key="'reach-' + r.field.key"
                v-bind="fieldProps(r.field)"
                bare
                :show-label="editing"
                v-on="fieldListeners"
              >
                <template #default="{ value }">
                  <span v-if="!reachEntries(value).length" class="ip-reach__empty">
                    {{ $t("itemProfile.addValue") }}
                  </span>
                  <div v-else class="ip-reach">
                    <template v-for="(entry, idx) in reachEntries(value)">
                      <div
                        v-if="r.kind === 'copy'"
                        :key="r.field.key + '-' + idx"
                        class="ip-reach__link ip-reach__link--copy"
                      >
                        <span v-if="!editing" class="ip-reach__label">{{ $t(r.field.label) }}</span>
                        <span class="ip-reach__text ip-reach__code">{{ entry }}</span>
                        <button
                          v-if="!editing"
                          type="button"
                          class="btn btn-sm btn-link mb-0 px-1 ip-reach__copy"
                          :title="$t('itemProfile.copy')"
                          @click.stop="copy(r.field.key + idx, entry)"
                        >
                          <i :class="copiedKey === r.field.key + idx ? 'fas fa-check' : 'far fa-copy'"></i>
                        </button>
                      </div>
                      <a
                        v-else
                        :key="r.field.key + '-' + idx"
                        class="ip-reach__link"
                        :href="reachHref(r.kind, entry)"
                        :target="r.kind === 'web' ? '_blank' : null"
                        :rel="r.kind === 'web' ? 'noopener' : null"
                        :title="$t(r.field.label)"
                        @click="editing && $event.preventDefault()"
                      >
                        <i :class="reachIcon(r.kind)" class="ip-reach__icon"></i>
                        <span class="ip-reach__text">{{ entry }}</span>
                      </a>
                    </template>
                  </div>
                </template>
              </ItemProfileField>
            </div>
          </div>

          <div v-if="!editing && metaFields.length" class="ip-hero__meta">
            <div v-for="m in metaFields" :key="'meta-' + m.field.key" class="ip-hero__meta-item">
              <i v-if="!editing" :class="m.icon" class="ip-hero__meta-icon"></i>
              <ItemProfileField
                v-bind="fieldProps(m.field)"
                bare
                :show-label="editing"
                v-on="fieldListeners"
              />
            </div>
          </div>

          <p v-if="$slots.footnote" class="ip-hero__footnote"><slot name="footnote" /></p>
        </div>

        <div v-if="getActionsEl" class="ip-hero__side">
        <!-- Acá aloja la página su barra de acciones (ver getActionsEl) -->
        <div v-show="hasActions" ref="actionsSlot" class="ip-hero__actions"></div>
        </div>
      </div>
      <div v-if="!editing && isTransaction && (layout.from || layout.to)" class="ip-flow">
        <!-- Editando, la punta es su campo (buscador de la referencia) -->
        <ItemProfileField
          v-if="layout.from && editing && fieldsByKey[layout.from.key]"
          v-bind="fieldProps(fieldsByKey[layout.from.key])"
          class="ip-flow__edit"
          :show-label="true"
          v-on="fieldListeners"
        />
        <ItemProfileParty
          v-else-if="layout.from"
          :value="item[layout.from.key]"
          :model="layout.from.model"
          :label="partyLabel(layout.from)"
          :lists="lists"
        />
        <div
          v-if="layout.from && layout.to"
          class="ip-flow__arrow"
          :class="txDirection === 'in' ? 'ip-flow__arrow--in' : 'ip-flow__arrow--out'"
        >
          <i class="fas fa-long-arrow-alt-right"></i>
        </div>
        <!-- Editando, la punta es su campo (buscador de la referencia) -->
        <ItemProfileField
          v-if="layout.to && editing && fieldsByKey[layout.to.key]"
          v-bind="fieldProps(fieldsByKey[layout.to.key])"
          class="ip-flow__edit"
          :show-label="true"
          v-on="fieldListeners"
        />
        <ItemProfileParty
          v-else-if="layout.to"
          :value="item[layout.to.key]"
          :model="layout.to.model"
          :label="partyLabel(layout.to)"
          :lists="lists"
        />
      </div>
      <nav v-if="tabs.length > 1" class="ip-tabs" role="tablist">
        <button
          v-for="t in tabs"
          :key="'tab-' + t.key"
          type="button"
          role="tab"
          class="ip-tabs__tab"
          :class="{ 'ip-tabs__tab--active': t.key === activeTab }"
          :aria-selected="t.key === activeTab ? 'true' : 'false'"
          @click="$emit('update:activeTab', t.key)"
        >
          <i v-if="t.icon" :class="t.icon" class="me-2"></i>{{ t.label }}
        </button>
      </nav>
    </section>

    <div v-if="activeTab !== 'profile'" class="ip-tab-panel">
      <slot :name="'tab-' + activeTab" />
    </div>

    <div v-else class="row">
      <div :class="sideSections.length || showWebProfile ? 'col-lg-8' : 'col-12'">
        <section v-for="f in aboutFields" :key="'about-' + f.key" class="card ip-card">
          <header class="ip-card__header">
            <i class="fas fa-align-left ip-card__icon"></i>
            {{ $t(f.label) }}
            <ColumnFieldHelpIcon
              v-if="f.tooltip"
              class="ip-help"
              :tooltip="f.tooltip"
              :aria-label="$t('ColumnFieldHelpAria')"
              popover-prefer-right
            />
          </header>
          <div class="ip-card__body">
            <ItemProfileField
              v-bind="fieldProps(f)"
              :show-label="false"
              class="ip-about"
              v-on="fieldListeners"
            />
          </div>
        </section>

        <section
          v-for="section in mainSections"
          :key="'main-' + section.name"
          class="card ip-card"
          :class="{ 'ip-card--lead': generalFields.length && isGeneralSection(section) }"
        >
          <header class="ip-card__header">
            <GroupIcon v-if="section.icon" class="ip-card__icon" :icon="section.icon" />
            <i v-else class="fas fa-info-circle ip-card__icon"></i>
            {{ $t(section.label) }}
          </header>
          <div class="ip-card__body ip-grid">
            <ItemProfileField
              v-for="f in section.fields"
              :key="section.name + '-' + f.key"
              v-bind="fieldProps(f)"
              :class="{ 'ip-grid__wide': isWide(f) }"
              :show-label="true"
              v-on="fieldListeners"
            >
              <!-- Emails, teléfonos, web, códigos: un valor por renglón (la vista de texto no muestra listas) -->
              <template v-if="reachKeySet.has(f.key)" #default="{ value }">
                <div v-for="(entry, idx) in reachEntries(value)" :key="f.key + '-r-' + idx">{{ entry }}</div>
              </template>
            </ItemProfileField>
          </div>
        </section>
      </div>

      <div v-if="sideSections.length || showWebProfile" class="col-lg-4">
        <!-- Sin encabezado: el bloque habla por sí solo. -->
        <section v-if="showWebProfile" class="card ip-card">
          <div class="ip-card__body ip-card__body--solo">
            <WebProfileCard
              :model-name="modelName"
              :item-id="String(item._id)"
              :item="item"
              :lists="lists"
              @image-set="$emit('saved', { key: 'image' })"
              @apply="applyListValues"
              @lists-changed="$emit('lists-changed')"
            />
          </div>
        </section>
        <section
          v-for="section in sideSections"
          :key="'side-' + section.name"
          class="card ip-card"
        >
          <header class="ip-card__header">
            <GroupIcon v-if="section.icon" class="ip-card__icon" :icon="section.icon" />
            <i v-else class="fas fa-info-circle ip-card__icon"></i>
            {{ $t(section.label) }}
          </header>
          <div class="ip-card__body" :class="section.stat ? 'ip-stats' : 'ip-stack'">
            <ItemProfileField
              v-for="f in section.fields"
              :key="section.name + '-' + f.key"
              v-bind="fieldProps(f)"
              :class="{ 'ip-stat': section.stat }"
              :show-label="true"
              v-on="fieldListeners"
            />
          </div>
        </section>
      </div>
    </div>
  </div>
</template>

<script>
import Swal from "sweetalert2";
import { wencesApi } from "@/api/wencesApi";
import GroupIcon from "@/components/gotrading/common/GroupIcon.vue";
import ColumnFieldHelpIcon from "@/components/gotrading/common/ColumnFieldHelpIcon.vue";
import {
  getApiErrorData,
  getApiErrorMessage,
  isHookValidationError,
  promptHookValidationForceSave,
} from "@/utils/dynamicModelValidationSwal";
import ItemProfileField, {
  canInlineEdit,
  isEmptyFieldValue,
} from "./ItemProfileField.vue";
import ItemProfileParty from "./ItemProfileParty.vue";
import WebProfileCard from "./WebProfileCard.vue";
import { getProfileLayout } from "./profileLayouts";
import { computeRuleDerivedValues, ruleSourceKeys, ruleTargetKeys } from "./profileRules";
import { getTranslatedListLabel } from "@/utils/listItemI18n";
import { requiredFieldsFor } from "@/utils/missingRequiredFields";
import { nameInitials } from "@/utils/nameInitials";

/*
 * Ficha de un item (Contact, Product, BankAccount). La disposición no sale de la
 * definición del modelo sino de profileLayouts.js; cada campo se edita en línea
 * y se guarda solo (partialUpdate).
 */
// Encabezado y márgenes de una tarjeta, medidos en renglones de campo.
const CARD_H = 1.6;

export default {
  name: "ItemProfile",
  components: { ItemProfileField, ItemProfileParty, GroupIcon, WebProfileCard, ColumnFieldHelpIcon },
  props: {
    item: { type: Object, required: true },
    fields: { type: Array, default: () => [] },
    visualGroups: { type: Array, default: () => [] },
    lists: { type: Array, default: () => [] },
    models: { type: Array, default: () => [] },
    modelName: { type: String, required: true },
    editing: { type: Boolean, default: false },
    /** Contornos de todos los campos editables a la vista (botón «Editar»); sin él, al pasar el mouse. */
    highlight: { type: Boolean, default: false },
    /** Permisos por rol de cada visual group (los mismos del detalle estándar) */
    canViewGroup: { type: Function, default: () => true },
    /** Rol que ve el grupo pero no lo edita (roleRestrictions.canEdit): sus campos van en gris. */
    canEditGroup: { type: Function, default: () => true },
    /** Reglas de cálculo de la definición: se corren al guardar (ver profileRules) */
    rules: { type: Array, default: () => [] },
    /** Pestañas bajo el encabezado ({ key, label, icon }); "profile" son los datos. */
    tabs: { type: Array, default: () => [] },
    activeTab: { type: String, default: "profile" },
    /**
     * Devuelve el nodo con la barra de acciones de la página (Refresh, Edit, More
     * actions…). La ficha lo muda a su encabezado, al lado del logo, y lo devuelve
     * a su lugar al desmontarse: así no se duplica la barra con todas sus condiciones.
     */
    getActionsEl: { type: Function, default: null },
  },
  data() {
    return {
      activeKey: null,
      savingKey: null,
      copiedKey: null,
      hasActions: false,
    };
  },
  computed: {
    layout() {
      return (
        getProfileLayout(this.modelName) || {
          avatarIcon: null,
          tagKeys: [],
          metaKeys: [],
          reachKeys: [],
          aboutKeys: [],
          sideGroups: [],
          groupByKey: {},
          stats: null,
        }
      );
    },
    isTransaction() {
      return this.layout.hero === "transaction";
    },
    /**
     * Dirección del dinero: fija en el layout (pago = out, cobro = in) o leída de un
     * campo (notas comerciales: flow_direction Giving / Receiving).
     */
    txDirection() {
      const l = this.layout;
      if (l.directionKey) {
        return (l.directionMap || {})[this.item[l.directionKey]] || l.direction || "out";
      }
      return l.direction || "out";
    },
    txKindLabel() {
      const labels = this.layout.kindLabels;
      if (labels && labels[this.txDirection]) return labels[this.txDirection];
      return this.txDirection === "in" ? "itemProfile.incoming" : "itemProfile.outgoing";
    },
    formattedAmount() {
      return this.formatMoney(this.item[this.layout.amountKey]);
    },
    /** Cifras de solo lectura bajo el monto (aplicado, pendiente), con su moneda. */
    amountNotes() {
      return (this.layout.amountNoteKeys || [])
        .map((key) => this.heroField(key))
        .filter((f) => f && !isEmptyFieldValue(this.item[f.key]))
        .map((f) => ({
          key: f.key,
          label: this.$t(f.label),
          value: [this.formatMoney(this.item[f.key]), this.currencyLabel].filter(Boolean).join(" "),
        }));
    },
    /** Monto y moneda del encabezado, cuando se editan en línea. */
    amountField() {
      const f = this.heroField(this.layout.amountKey);
      return f && canInlineEdit(f) ? f : null;
    },
    currencyField() {
      const f = this.heroField(this.layout.currencyKey);
      return f && canInlineEdit(f) ? f : null;
    },
    currencyLabel() {
      const f = this.fieldsByKey[this.layout.currencyKey];
      return f ? this.listLabel(f.listType, this.item[f.key]) : "";
    },
    /**
     * Solo el estado confirmado (pagado / cobrado). «Falso» no alcanza para decir
     * pendiente: hay tenants que no usan el campo (nibiru paga sin etapa de
     * solicitud y casi todos sus pagos tienen paid=false), y ahí sería mentira.
     */
    txStatus() {
      const key = this.layout.statusKey;
      const dateKey = this.layout.statusDateKey;
      const done =
        (key && this.item[key] === true) || (dateKey && !!this.item[dateKey]);
      if (!done) return null;
      return {
        tone: "done",
        icon: "fas fa-check-circle",
        label: this.txDirection === "in" ? "itemProfile.statusReceived" : "itemProfile.statusPaid",
      };
    },
    fieldsByKey() {
      const map = {};
      for (const f of this.fields) {
        if (f && f.key) map[f.key] = f;
      }
      return map;
    },
    groupsByName() {
      const map = {};
      for (const g of this.visualGroups || []) {
        if (g && g.name) map[g.name] = g;
      }
      return map;
    },
    tagFields() {
      // El campo de dirección ya se lee en la flecha; editando aparece para cambiarlo.
      const keys = [...this.layout.tagKeys];
      if (this.editing && this.layout.directionKey) keys.push(this.layout.directionKey);
      return keys.map((k) => this.heroField(k)).filter(Boolean);
    },
    metaFields() {
      return this.layout.metaKeys
        .map((m) => ({ ...m, field: this.heroField(m.key) }))
        .filter((m) => m.field);
    },
    reachFields() {
      return this.layout.reachKeys
        .map((r) => ({ ...r, field: this.heroField(r.key) }))
        .filter(
          (r) => r.field && (this.editing || this.reachEntries(this.item[r.key]).length)
        );
    },
    /**
     * Editando, los campos del encabezado (etiquetas, contactos, fechas, desde → hacia)
     * pasan a una tarjeta «General»: con sus recuadros el encabezado crecía tanto que
     * las pestañas quedaban muy abajo. Arriba quedan el nombre y el monto.
     */
    generalFields() {
      if (!this.editing) return [];
      const l = this.layout;
      const parties = this.isTransaction
        ? [l.from, l.to].filter(Boolean).map((p) => this.heroField(p.key))
        : [];
      const seen = new Set();
      return [
        ...this.tagFields,
        ...parties,
        ...this.metaFields.map((m) => m.field),
        ...this.reachFields.map((r) => r.field),
      ].filter((f) => f && !seen.has(f.key) && seen.add(f.key));
    },
    /** Sin edición (nombre armado por el sistema) va sin rótulo: es el título. */
    nameEditable() {
      const f = this.heroField("name");
      return !!f && canInlineEdit(f);
    },
    layoutHiddenKeys() {
      const fn = this.layout.hiddenKeys;
      return new Set(typeof fn === "function" ? fn(this.item) : []);
    },
    calculatedKeys() {
      return ruleTargetKeys(this.rules, this.visualGroups);
    },
    reachKeySet() {
      return new Set(this.layout.reachKeys.map((r) => r.key));
    },
    aboutFields() {
      return this.layout.aboutKeys.map((k) => this.heroField(k)).filter(Boolean);
    },
    /** Campos con lugar fijo: no se repiten en las tarjetas. */
    placedKeys() {
      const l = this.layout;
      return new Set([
        "name",
        "image",
        ...[l.amountKey, l.currencyKey, l.statusKey, l.statusDateKey, l.directionKey].filter(Boolean),
        ...(l.amountNoteKeys || []),
        ...[l.from, l.to].filter(Boolean).map((p) => p.key),
        ...l.tagKeys,
        ...l.metaKeys.map((m) => m.key),
        ...l.reachKeys.map((r) => r.key),
        ...l.aboutKeys,
      ]);
    },
    imageField() {
      return this.fieldsByKey.image || null;
    },
    avatarSrc() {
      const raw = this.imageField ? this.item.image : null;
      const first = Array.isArray(raw) ? raw[0] : raw;
      if (!first) return null;
      if (typeof first === "string") return first;
      return first.url || first.path || first.src || null;
    },
    initials() {
      return nameInitials(this.item.name);
    },
    statKeys() {
      const stats = this.layout.stats;
      return new Set(stats && stats.keys ? stats.keys : []);
    },
    sections() {
      const { groupByKey, sideGroups, stats } = this.layout;
      const byGroup = {};
      const loose = [];
      const statFields = [];
      const fileFields = [];
      const filesSection = this.layout.filesSection;
      for (const f of this.fields) {
        if (!f || !f.key || !this.isShown(f)) continue;
        if (this.statKeys.has(f.key)) {
          statFields.push(f);
          continue;
        }
        if (this.placedKeys.has(f.key)) continue;
        if (filesSection && (f.type === "file" || f.type === "image")) {
          fileFields.push(f);
          continue;
        }
        const group =
          this.groupsByName[groupByKey[f.key]] ||
          (f.visualGroup && this.groupsByName[f.visualGroup]);
        if (group) {
          if (!this.canViewGroup(group)) continue;
          (byGroup[group.name] = byGroup[group.name] || []).push(f);
        } else {
          loose.push(f);
        }
      }
      // Editando, lo del encabezado va primero en el grupo «General» del modelo (o en uno propio).
      if (this.generalFields.length) {
        const own = (this.visualGroups || []).find(
          (g) => g && String(g.name || "").toLowerCase() === "general" && this.canViewGroup(g)
        );
        if (own) byGroup[own.name] = [...this.generalFields, ...(byGroup[own.name] || [])];
      }
      const ordered = Object.keys(byGroup)
        .map((name) => this.groupsByName[name])
        .sort((a, b) => (a.order || 0) - (b.order || 0))
        .map((g) => ({
          name: g.name,
          label: g.label || g.name,
          icon: g.icon || (stats && stats.group === g.name ? stats.icon : null),
          side: sideGroups.includes(g.name),
          stat: !!(stats && stats.group === g.name),
          fields: byGroup[g.name],
        }));
      if (this.generalFields.length && !ordered.some((sec) => String(sec.name).toLowerCase() === "general")) {
        ordered.unshift({
          name: "_general",
          label: "itemProfile.general",
          icon: null,
          side: false,
          stat: false,
          fields: this.generalFields,
        });
      }
      if (statFields.length) {
        ordered.unshift({
          name: stats.name,
          label: stats.label,
          icon: stats.icon,
          side: true,
          stat: true,
          fields: statFields,
        });
      }
      if (fileFields.length) {
        ordered.push({
          name: "_files",
          label: filesSection.label,
          icon: filesSection.icon,
          side: true,
          stat: false,
          fields: fileFields,
        });
      }
      if (loose.length) {
        ordered.push({
          name: "_more",
          label: this.visualGroups.length
            ? "itemProfile.moreInfo"
            : "itemProfile.details",
          icon: null,
          side: false,
          stat: false,
          fields: loose,
        });
      }
      return ordered;
    },
    /**
     * Reparto de tarjetas entre columnas. La disposición dice cuáles van a la
     * derecha, pero si eso deja la derecha mucho más alta (p. ej. una cuenta con
     * General corto e intermediario + beneficiario), las tarjetas laterales que
     * no son cifras pasan a la izquierda mientras eso empareje las columnas.
     */
    columns() {
      const main = this.sections.filter((s) => !s.side);
      // Las cifras primero: es lo que se busca de un vistazo.
      const side = this.sections
        .filter((s) => s.side)
        .sort((a, b) => Number(b.stat) - Number(a.stat));
      // La columna derecha es de «En la web», que es larga: debajo nadie vería nada.
      if (this.showWebProfile) return { main: [...main, ...side], side: [] };
      let mainH =
        this.aboutFields.reduce((h) => h + CARD_H + 1.5, 0) +
        main.reduce((h, sec) => h + this.sectionHeight(sec, false), 0);
      let sideH = side.reduce((h, sec) => h + this.sectionHeight(sec, true), 0);
      const moved = [];
      for (let i = side.length - 1; i >= 0; i -= 1) {
        const sec = side[i];
        if (sec.stat) continue;
        const asSide = this.sectionHeight(sec, true);
        const asMain = this.sectionHeight(sec, false);
        const before = Math.abs(sideH - mainH);
        const after = Math.abs(sideH - asSide - (mainH + asMain));
        if (after >= before) break;
        side.splice(i, 1);
        moved.unshift(sec);
        sideH -= asSide;
        mainH += asMain;
      }
      return { main: [...main, ...moved], side };
    },
    mainSections() {
      return this.columns.main;
    },
    sideSections() {
      return this.columns.side;
    },
    /** Lo que se sabe en la web (contactos y productos ya guardados; se busca al abrir la ficha). */
    showWebProfile() {
      return ["Contact", "Product"].includes(this.modelName) && !!(this.item && this.item._id);
    },
    fieldListeners() {
      return {
        activate: this.onActivate,
        deactivate: this.onDeactivate,
        save: this.saveField,
      };
    },
    /**
     * Obligatorios de este registro, con el criterio de la validación del back: los
     * `required` más los de «Hacer obligatorio» (Logistic team si el Type es Client).
     */
    requiredFields() {
      const name = String(this.modelName || "").toLowerCase();
      const def = this.models.find((m) => String((m && m.modelName) || "").toLowerCase() === name);
      const required = requiredFieldsFor(
        { fields: this.fields, visualRules: (def && def.visualRules) || [] },
        this.item
      );
      return required.filter((f) => {
        const group = f.visualGroup && this.groupsByName[f.visualGroup];
        return !group || this.canViewGroup(group);
      });
    },
    requiredKeys() {
      return new Set(this.requiredFields.map((f) => f.key));
    },
    /** Los obligatorios vacíos: van en la franja de arriba y en rojo en su campo. */
    missingRequired() {
      return this.requiredFields.filter((f) => isEmptyFieldValue(this.item[f.key]));
    },
    missingRequiredKeys() {
      return new Set(this.missingRequired.map((f) => f.key));
    },
  },
  mounted() {
    this.adoptActions();
  },
  beforeDestroy() {
    this.releaseActions();
  },
  watch: {
    editing(isEditing) {
      if (!isEditing) this.activeKey = null;
      // Se edita sobre los datos: volver a esa pestaña.
      if (isEditing && this.activeTab !== "profile") this.$emit("update:activeTab", "profile");
    },
  },
  methods: {
    heroField(key) {
      const f = this.fieldsByKey[key];
      if (!f) return null;
      // El nombre siempre: es el título de la ficha.
      if (key === "name") return f.hidedisplay ? null : f;
      if (!this.isShown(f)) return null;
      const group = f.visualGroup && this.groupsByName[f.visualGroup];
      if (group && !this.canViewGroup(group)) return null;
      return f;
    },
    /** Misma regla que el detalle: hidedisplay oculta; vacío solo se ve editando. */
    isShown(f) {
      if (f.hidedisplay || f.hideDisplay || f.type === "button") return false;
      if (this.layoutHiddenKeys.has(f.key)) return false;
      const empty = isEmptyFieldValue(this.item[f.key]);
      if (!this.editing) return !empty;
      return !empty || canInlineEdit(f);
    },
    /** Alto aproximado de una tarjeta, en renglones de campo. */
    sectionHeight(section, inSide) {
      if (inSide) {
        return CARD_H + section.fields.length * (section.stat ? 1.1 : 1);
      }
      // Izquierda: grilla de dos columnas; los anchos ocupan el renglón entero.
      let rows = 0;
      let half = 0;
      for (const f of section.fields) {
        if (this.isWide(f)) rows += 1;
        else half += 1;
      }
      return CARD_H + rows + Math.ceil(half / 2);
    },
    isGeneralSection(section) {
      const name = String(section.name || "").toLowerCase();
      return name === "general" || name === "_general";
    },
    isWide(f) {
      return f.type === "textarea" || f.type === "group";
    },
    /** Lo calcula una regla (como en el formulario, va en gris) o el rol no edita su grupo. */
    isBlocked(f) {
      if (this.calculatedKeys.has(f.key)) return true;
      const group =
        this.groupsByName[this.layout.groupByKey[f.key]] ||
        (f.visualGroup && this.groupsByName[f.visualGroup]);
      return !!group && !this.canEditGroup(group);
    },
    fieldProps(f) {
      return {
        blocked: this.isBlocked(f),
        field: f,
        item: this.item,
        lists: this.lists,
        models: this.models,
        modelName: this.modelName,
        editing: this.editing,
        active: this.activeKey === f.key,
        saving: this.savingKey === f.key,
        required: this.requiredKeys.has(f.key),
        missing: this.missingRequiredKeys.has(f.key),
      };
    },
    /** Desde la franja de obligatorios: se completa en la ficha si se puede editar ahí. */
    canFillMissing(f) {
      return this.editing && !this.isBlocked(f) && canInlineEdit(f) && this.isShown(f);
    },
    fillMissing(key) {
      this.onActivate(key);
      this.$nextTick(() => {
        const el = this.$el && this.$el.querySelector(".ip-field--active");
        if (el && el.scrollIntoView) el.scrollIntoView({ block: "center", behavior: "smooth" });
      });
    },
    /** Valores mostrables: las importaciones dejaron «HTTP://» sueltos. */
    reachEntries(value) {
      return (Array.isArray(value) ? value : [value])
        .map((v) => (v === null || v === undefined ? "" : String(v).trim()))
        .filter((v) => v && !/^https?:\/*$/i.test(v));
    },
    reachIcon(kind) {
      if (kind === "phone") return "fas fa-phone";
      if (kind === "email") return "fas fa-envelope";
      return "fas fa-link";
    },
    reachHref(kind, entry) {
      const text = String(entry).trim();
      if (kind === "email") return `mailto:${text}`;
      if (kind === "phone") return `tel:${text.replace(/[^\d+]/g, "")}`;
      return /^https?:\/\//i.test(text) ? text : `https://${text}`;
    },
    formatMoney(value) {
      const n = Number(value);
      if (value === null || value === undefined || value === "" || !Number.isFinite(n)) return "-";
      const locale = (this.$i18n && this.$i18n.locale) || "en";
      return n.toLocaleString(locale === "es" ? "es-AR" : "en-US", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 4,
      });
    },
    listLabel(listType, value) {
      if (!value) return "";
      const locale = this.$i18n && this.$i18n.locale;
      if (typeof value === "object" && (value.label || value.labels)) {
        return getTranslatedListLabel(value, locale);
      }
      const id = typeof value === "object" ? value._id : value;
      const list = this.lists.find((l) => l.type === listType);
      const opt = list && (list.values || []).find((o) => String(o._id) === String(id));
      return opt ? getTranslatedListLabel(opt, locale) : "";
    },
    partyLabel(party) {
      const f = this.fieldsByKey[party.key];
      return f ? this.$t(f.label) : "";
    },
    adoptActions() {
      const el = this.getActionsEl ? this.getActionsEl() : null;
      const slot = this.$refs.actionsSlot;
      if (!el || !slot || el.parentNode === slot) return;
      this.actionsHome = el.parentNode;
      slot.appendChild(el);
      this.hasActions = true;
    },
    releaseActions() {
      const el = this.getActionsEl ? this.getActionsEl() : null;
      if (el && this.actionsHome && el.parentNode !== this.actionsHome) {
        this.actionsHome.appendChild(el);
      }
      this.actionsHome = null;
      this.hasActions = false;
    },
    async copy(key, text) {
      try {
        await navigator.clipboard.writeText(String(text));
        this.copiedKey = key;
        setTimeout(() => {
          if (this.copiedKey === key) this.copiedKey = null;
        }, 1500);
      } catch (_e) {
        /* sin permiso de portapapeles: el valor sigue a la vista */
      }
    },
    onActivate(key) {
      if (this.savingKey) return;
      this.activeKey = key;
    },
    onDeactivate(key) {
      if (this.activeKey === key) this.activeKey = null;
    },
    /** «En la web» del producto: suma a un campo de lista múltiple los ids confirmados. */
    async applyListValues({ key, ids, done }) {
      const raw = this.item[key];
      const current = (Array.isArray(raw) ? raw : raw ? [raw] : []).map((v) => String((v && v._id) || v));
      const value = Array.from(new Set([...current, ...ids.map(String)]));
      done(await this.saveField({ key, value }));
    },
    /** Devuelve si se guardó. */
    /**
     * Documento para correr las reglas: lo guardado + el cambio, con las filas de
     * los grupos que las reglas leen como objetos (si el item trae solo ids, se piden).
     */
    async docForRules(changes) {
      const doc = { ...this.item, ...changes };
      const sources = ruleSourceKeys(this.rules, this.visualGroups);
      for (const key of sources) {
        const f = this.fieldsByKey[key];
        if (!f || f.type !== "group" || !Array.isArray(doc[key])) continue;
        if (!doc[key].some((r) => typeof r === "string")) continue;
        const groupModel = `${this.modelName}_${key}`;
        doc[key] = await Promise.all(
          doc[key].map((r) =>
            typeof r === "string"
              ? wencesApi
                  .get(`/api/gotrading/dynamic/${encodeURIComponent(groupModel)}/${encodeURIComponent(r)}`)
                  .then((res) => res.data)
              : r
          )
        );
      }
      return doc;
    },
    async saveField({ key, value, extra }) {
      if (this.savingKey) return false;
      this.savingKey = key;
      try {
        const changes = { ...(extra || {}), [key]: value };
        // Las reglas del formulario (aplicado, saldo…) también corren acá; lo que
        // recalculan viaja en el mismo guardado y el back valida el conjunto.
        let derived = {};
        if (this.rules.length) {
          derived = computeRuleDerivedValues({
            doc: await this.docForRules(changes),
            before: this.item,
            rules: this.rules,
            visualGroups: this.visualGroups,
            changedKeys: Object.keys(changes),
          });
        }
        const url = `/api/gotrading/dynamic/${encodeURIComponent(
          this.modelName
        )}/${encodeURIComponent(this.item._id)}`;
        // validatePartial: el back valida el documento entero (obligatorios, reglas
        // de negocio del modelo) aunque se guarde un solo campo.
        const body = { ...derived, ...changes, partialUpdate: true, validatePartial: true };
        try {
          await wencesApi.put(url, body);
        } catch (error) {
          // Los avisos blandos del hook (nota recibida sin adjunto, monto pendiente…)
          // llevan el mismo cartel con «guardar igual» que el formulario.
          const data = getApiErrorData(error);
          if (!data || !isHookValidationError(data)) throw error;
          const confirmed = await promptHookValidationForceSave(
            (k, params) => this.$t(k, params),
            data.message,
            { modelName: this.modelName }
          );
          if (!confirmed) return false;
          await wencesApi.put(url, { ...body, forceSave: true });
        }
        if (this.activeKey === key) this.activeKey = null;
        this.$emit("saved", { key, value });
        return true;
      } catch (error) {
        Swal.fire({
          icon: "error",
          title: this.$t("itemProfile.saveError"),
          text: getApiErrorMessage(error) || "",
        });
        return false;
      } finally {
        this.savingKey = null;
      }
    },
  },
};
</script>

<style scoped>
.ip {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  margin-top: 0.75rem;
}

.ip .card {
  margin-bottom: 0;
  border: 1px solid var(--bs-border-color);
  border-radius: 0.75rem;
  box-shadow: var(--bs-box-shadow-sm);
}

.ip > .row {
  row-gap: 1rem;
}

.ip > .row > [class*="col-"] {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

/* Obligatorios vacíos (#473): mismo formato que el aviso de «Editar», en rojo */
.ip-missing {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.25rem;
  padding: 0.5rem 0.9rem;
  border: 1px solid rgba(var(--bs-danger-rgb), 0.45);
  border-radius: 0.75rem;
  background-color: rgba(var(--bs-danger-rgb), 0.06);
  color: var(--bs-danger);
  font-size: 0.875rem;
}

.ip-missing__field {
  font-size: inherit;
  font-weight: 600;
  color: var(--bs-danger);
  text-decoration: underline;
}

/* Aviso del botón «Editar» (o solo el link al formulario completo) */
.ip-edit-hint {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.25rem;
  padding: 0.5rem 0.9rem;
  border: 1px dashed rgba(var(--bs-primary-rgb), 0.6);
  border-radius: 0.75rem;
  background-color: rgba(var(--bs-primary-rgb), 0.06);
  color: var(--bs-primary);
  font-size: 0.875rem;
}

.ip--highlight :deep(.ip-field--editable) {
  border-color: rgba(var(--bs-primary-rgb), 0.45);
}

.ip--highlight :deep(.ip-field--editable .ip-field__icon) {
  opacity: 1;
}

/* Encabezado */
/* Sin overflow: hidden: los desplegables del editor tienen que poder salir de la tarjeta. */
.ip-hero__body {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-start;
  gap: 1rem 1.5rem;
  padding: 1.5rem;
}

.ip-hero__avatar {
  flex: 0 0 auto;
  width: 6.5rem;
  height: 6.5rem;
  border-radius: 1.25rem;
  border: 1px solid var(--bs-border-color);
  background-color: var(--bs-tertiary-bg);
  color: var(--bs-primary);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 2.1rem;
  font-weight: 700;
  letter-spacing: 0.02em;
  overflow: hidden;
  box-shadow: var(--bs-box-shadow-sm);
}

/* Casi siempre es un logo y rara vez cuadrado: entero, sobre blanco (como se diseñan). */
.ip-hero__avatar--img {
  background-color: #fff;
  padding: 0.5rem;
}

.ip-hero__avatar img {
  width: 100%;
  height: 100%;
  object-fit: contain;
}

.ip-hero__main {
  flex: 1 1 22rem;
  min-width: 0;
  padding-top: 0.35rem;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.ip-hero__name {
  margin: 0;
  font-size: 1.6rem;
  font-weight: 700;
  line-height: 1.2;
}

.ip-hero__tags {
  display: flex;
  flex-wrap: wrap;
  gap: 0.4rem;
}

.ip-hero__tag :deep(.ip-field__value) {
  display: inline-flex;
}

.ip:not(.ip--editing) .ip-hero__tag {
  padding: 0.15rem 0.7rem;
  border-radius: 999px;
  background-color: rgba(var(--bs-primary-rgb), 0.1);
  color: var(--bs-primary);
  font-size: 0.8rem;
  font-weight: 600;
}

.ip-hero__meta {
  display: flex;
  flex-wrap: wrap;
  gap: 0.35rem 1.25rem;
  color: var(--bs-secondary-color);
}

.ip-hero__meta-item {
  display: flex;
  align-items: baseline;
  gap: 0.45rem;
  min-width: 0;
}

.ip-hero__meta-icon {
  color: var(--bs-primary);
  width: 1rem;
  text-align: center;
}

.ip-hero__footnote {
  margin: 0.15rem 0 0;
  font-size: 0.78rem;
  color: var(--bs-secondary-color);
}

#darkdesign .ip-hero__footnote {
  color: var(--dark-text-secondary);
}

/* Columna derecha: arriba la barra de acciones de la página, debajo los contactos/códigos */
.ip-hero__side {
  flex: 0 1 22rem;
  min-width: 0;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 0.85rem;
}

.ip-hero__actions :deep(.gt-model-actions-toolbar) {
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-end;
  align-items: center;
  gap: 0.35rem;
  margin: 0 !important;
}

.ip-hero__actions :deep(.gt-model-actions-toolbar > *) {
  float: none !important;
  margin: 0 !important;
}

.ip-hero__reach {
  min-width: 0;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.35rem;
}

.ip-hero__tagline {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.35rem 0.75rem;
}

.ip-hero__tagline > .ip-hero__tags {
  margin: 0;
}

/* Teléfono, email y web como chips del tamaño de su contenido: a todo el
   ancho un número de 15 caracteres ocupaba un recuadro enorme. */
.ip-reach {
  display: flex;
  flex-wrap: wrap;
  gap: 0.35rem;
}

.ip-reach__link {
  display: inline-flex;
  align-items: center;
  gap: 0.45rem;
  min-width: 0;
  max-width: 100%;
  padding: 0.25rem 0.6rem;
  font-size: 0.875rem;
  border-radius: 0.6rem;
  border: 1px solid var(--bs-border-color);
  color: inherit;
  text-decoration: none;
  transition: border-color 0.15s, background-color 0.15s;
}

.ip-reach__link:hover {
  border-color: var(--bs-primary);
  background-color: rgba(var(--bs-primary-rgb), 0.06);
}

.ip-reach__empty {
  color: var(--bs-secondary-color);
  font-style: italic;
}

#darkdesign .ip-reach__empty {
  color: var(--dark-text-secondary);
}

.ip-reach__icon {
  color: var(--bs-primary);
  width: 1rem;
  text-align: center;
}

.ip-reach__text {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.ip-hero__avatar i {
  font-size: 2.3rem;
}

.ip-reach__link--copy {
  flex-wrap: wrap;
  gap: 0 0.6rem;
}

.ip-reach__label {
  flex: 0 0 100%;
  font-size: 0.68rem;
  font-weight: 600;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: var(--bs-secondary-color);
}

.ip-reach__code {
  flex: 1 1 auto;
  font-family: var(--bs-font-monospace);
  font-size: 0.9rem;
  letter-spacing: 0.02em;
}

.ip-reach__copy {
  color: var(--bs-primary);
}

#darkdesign .ip-reach__label {
  color: var(--dark-text-secondary);
}

/* Cifras (stock, saldos): un renglón por cifra, que los montos largos no se corten */
.ip-stats {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.ip-stats > .ip-stat {
  margin: 0;
  padding: 0.55rem 0.8rem;
  border-radius: 0.6rem;
  background-color: rgba(var(--bs-primary-rgb), 0.06);
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
}

.ip-stat :deep(.ip-field__label) {
  margin: 0;
}

.ip-stat :deep(.ip-field__value) {
  font-size: 1.2rem;
  font-weight: 700;
  text-align: right;
  white-space: nowrap;
}

/* Pago / cobro */
.ip-tx__dir {
  font-size: 2.2rem;
}

.ip-tx__dir--out {
  color: var(--bs-danger);
  background-color: rgba(var(--bs-danger-rgb), 0.08);
  border-color: rgba(var(--bs-danger-rgb), 0.25);
}

.ip-tx__dir--in {
  color: var(--bs-success);
  background-color: rgba(var(--bs-success-rgb), 0.08);
  border-color: rgba(var(--bs-success-rgb), 0.25);
}

.ip-tx__kind {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.75rem;
  font-weight: 600;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  color: var(--bs-secondary-color);
}

.ip-tx__status {
  display: inline-flex;
  align-items: center;
  padding: 0.15rem 0.6rem;
  border-radius: 999px;
  letter-spacing: 0.02em;
}

.ip-tx__status--done {
  color: var(--bs-success);
  background-color: rgba(var(--bs-success-rgb), 0.12);
}

.ip-tx__amount {
  margin: 0;
  font-size: 2rem;
  font-weight: 700;
  line-height: 1.15;
  font-variant-numeric: tabular-nums;
}

.ip-tx__amount-edit {
  display: flex;
  flex-wrap: wrap;
  align-items: stretch;
  gap: 0.6rem;
}

.ip-tx__amount-field {
  flex: 1 1 14rem;
  min-width: 0;
}

.ip-tx__currency-field {
  flex: 0 1 11rem;
  min-width: 8rem;
}

.ip-tx__amount small {
  font-size: 1.1rem;
  font-weight: 600;
  color: var(--bs-secondary-color);
}

.ip-tx__notes {
  display: flex;
  flex-wrap: wrap;
  gap: 0.15rem 1.25rem;
  font-size: 0.85rem;
  color: var(--bs-secondary-color);
}

.ip-tx__note strong {
  margin-left: 0.25rem;
  font-weight: 600;
  color: var(--bs-body-color);
  font-variant-numeric: tabular-nums;
}

#darkdesign .ip-tx__notes {
  color: var(--dark-text-secondary);
}

#darkdesign .ip-tx__note strong {
  color: var(--dark-text-primary);
}


/* Nombre que no se edita: título de la ficha, sin rótulo */
.ip-tx__title--lead {
  margin: 0;
  word-break: break-word;
  color: var(--bs-body-color);
  font-size: 1.25rem;
  font-weight: 600;
  line-height: 1.3;
}

#darkdesign .ip-tx__title--lead {
  color: var(--dark-text-primary);
}

#darkdesign .ip-tx__kind,
#darkdesign .ip-tx__amount small {
  color: var(--dark-text-secondary);
}

/* Desde → hacia */
.ip-flow > .ip-flow__edit {
  flex: 1 1 0;
  min-width: 0;
  margin: 0;
  padding: 0.6rem 0.85rem;
}

.ip-flow {
  display: flex;
  align-items: stretch;
  gap: 0.75rem;
  padding: 0 1.5rem 1.5rem;
}

.ip-flow__arrow {
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 2.5rem;
  font-size: 1.6rem;
}

.ip-flow__arrow--out {
  color: var(--bs-danger);
}

.ip-flow__arrow--in {
  color: var(--bs-success);
}

/* Pestañas en el pie del encabezado: píldoras sobre una franja, que no se pierdan */
.ip-tabs {
  display: flex;
  gap: 0.5rem;
  padding: 0.65rem 1.25rem;
  border-top: 1px solid var(--bs-border-color);
  background-color: var(--bs-tertiary-bg);
  border-radius: 0 0 calc(0.75rem - 1px) calc(0.75rem - 1px);
  overflow-x: auto;
}

.ip-tabs__tab {
  flex: 0 0 auto;
  display: inline-flex;
  align-items: center;
  background: none;
  border: 1px solid transparent;
  border-radius: 999px;
  padding: 0.5rem 1.1rem;
  font-weight: 600;
  font-size: 0.95rem;
  color: var(--bs-body-color);
  transition: color 0.15s, background-color 0.15s, border-color 0.15s;
}

.ip-tabs__tab i {
  color: var(--bs-primary);
}

.ip-tabs__tab:hover {
  background-color: rgba(var(--bs-primary-rgb), 0.1);
  border-color: rgba(var(--bs-primary-rgb), 0.25);
}

.ip-tabs__tab--active,
.ip-tabs__tab--active:hover {
  background-color: var(--bs-primary);
  border-color: var(--bs-primary);
  color: var(--bs-white);
  box-shadow: var(--bs-box-shadow-sm);
}

.ip-tabs__tab--active i {
  color: inherit;
}

#darkdesign .ip-tabs {
  border-top-color: var(--dark-border-color);
  background-color: var(--dark-bg-tertiary);
}

#darkdesign .ip-tabs__tab:not(.ip-tabs__tab--active) {
  color: var(--dark-text-primary);
}

/* Tarjetas */
.ip-card__header {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.85rem 1.25rem 0;
  font-weight: 600;
  font-size: 1rem;
}

.ip-help :deep(.column-field-help-btn) {
  opacity: 0.7;
  color: var(--bs-primary);
}

.ip-card__icon {
  color: var(--bs-primary);
}

.ip-card__body {
  padding: 0.75rem 1.25rem 1.1rem;
}

/* Editando, «General» (con lo del encabezado) va primero, antes que los textos largos. */
.ip-card--lead {
  order: -1;
}

.ip-card__body--solo {
  padding-top: 1.1rem;
}

.ip-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0.6rem 2rem;
}

.ip-grid__wide {
  grid-column: 1 / -1;
}

.ip-stack {
  display: flex;
  flex-direction: column;
  gap: 0.6rem;
}

.ip-about {
  white-space: pre-line;
}

/* Editando, etiquetas y ubicación pasan a una grilla pareja: como chips quedaban apretados. */
.ip--editing .ip-hero__main {
  gap: 0.75rem;
}

.ip--editing .ip-hero__tags,
.ip--editing .ip-hero__meta {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(11rem, 1fr));
  gap: 0.6rem;
}

/* Como hijo del renglón flexible la grilla se encogía a una sola columna. */
.ip--editing .ip-hero__tagline > .ip-hero__tags {
  flex: 1 1 100%;
}

.ip--editing .ip-hero__meta-item {
  display: block;
}

.ip--editing .ip-hero__meta {
  color: inherit;
}

.ip--editing .ip-hero__reach {
  gap: 0.6rem;
}

.ip--editing .ip-hero :deep(.ip-field--bare) {
  padding: 0.5rem 0.75rem;
}

#darkdesign .ip-hero__meta {
  color: var(--dark-text-secondary);
}

#darkdesign .ip-hero__avatar {
  border-color: var(--dark-border-color);
  background-color: var(--dark-bg-tertiary);
}

#darkdesign .ip-hero__avatar--img {
  background-color: #fff;
}

#darkdesign .ip-reach__link {
  border-color: var(--dark-border-color);
}

@media (max-width: 767.98px) {
  .ip-hero__body {
    padding: 1rem;
    gap: 0.85rem 1rem;
  }

  /* Teléfono: logo y nombre en el mismo renglón; contactos debajo, a lo ancho */
  .ip-hero__avatar {
    width: 3.75rem;
    height: 3.75rem;
    border-radius: 0.9rem;
    font-size: 1.25rem;
  }

  .ip-hero__avatar i {
    font-size: 1.5rem;
  }

  .ip-hero__main {
    flex: 1 1 0;
    padding-top: 0;
  }

  .ip-hero__side {
    flex-basis: 100%;
  }

  .ip-tabs {
    padding: 0.5rem 0.75rem;
  }

  /* Teléfono: desde arriba, hacia abajo */
  .ip-flow {
    flex-direction: column;
    padding: 0 1rem 1rem;
  }

  .ip-flow__arrow {
    width: auto;
    height: 1.5rem;
    transform: rotate(90deg);
  }

  .ip-tx__amount {
    font-size: 1.5rem;
  }

  .ip-tabs__tab {
    padding: 0.45rem 0.85rem;
    font-size: 0.875rem;
  }

  .ip-hero__name {
    font-size: 1.3rem;
  }

  .ip-grid {
    grid-template-columns: minmax(0, 1fr);
  }
}
</style>
